import UpgradeBanner from "../components/UpgradeBanner";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, useAuthStore } from "../store/authStore";
import { toast } from "sonner";
import { ArrowLeft, Target, ArrowSquareOut, Spinner, PaperPlaneTilt, MagnifyingGlass, Plus } from "@phosphor-icons/react";

const S = {
  bg:'#0a0a0f', white:'#13131f', border:'rgba(124,58,237,0.2)', borderMd:'rgba(124,58,237,0.4)',
  text:'#e8e8f0', muted:'#9090b0', hint:'#5a5a7a', indigo:'#7c3aed',
};
const card = { background:S.white, border:`1px solid ${S.border}`, borderRadius:10 };

function StatusBadge({ status }) {
  const styles = {
    submitted:        { bg:'rgba(29,78,216,0.2)',  color:'#60a5fa' },
    approved:         { bg:'rgba(21,128,61,0.2)',  color:'#4ade80' },
    pending_approval: { bg:'rgba(161,98,7,0.2)',   color:'#fbbf24' },
    failed:           { bg:'rgba(185,28,28,0.2)',  color:'#f87171' },
    not_submitted:    { bg:'rgba(90,90,122,0.2)',  color:'#5a5a7a' },
    draft:            { bg:'rgba(90,90,122,0.2)',  color:'#5a5a7a' },
    sent:             { bg:'rgba(21,128,61,0.2)',  color:'#4ade80' },
    new:              { bg:'rgba(29,78,216,0.2)',  color:'#60a5fa' },
  };
  const s = styles[status] || { bg:'rgba(90,90,122,0.2)', color:'#5a5a7a' };
  return (
    <span style={{ fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:5, background:s.bg, color:s.color }}>
      {status.replace(/_/g,' ')}
    </span>
  );
}

export default function LinkBuilder() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();

  const [projects, setProjects]             = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tab, setTab]                       = useState("directories");
  const [loading, setLoading]               = useState(true);
  const [submissions, setSubmissions]       = useState([]);
  const [overview, setOverview]             = useState(null);
  const [submitting, setSubmitting]         = useState(false);
  const [outreachList, setOutreachList]     = useState([]);
  const [targetUrl, setTargetUrl]           = useState("");
  const [targetEmail, setTargetEmail]       = useState("");
  const [generating, setGenerating]         = useState(false);
  const [opportunities, setOpportunities]   = useState([]);
  const [competitorUrl, setCompetitorUrl]   = useState("");
  const [analyzing, setAnalyzing]           = useState(false);

  useEffect(() => {
    apiClient.get("/projects").then(data => {
      setProjects(data);
      const pid = searchParams.get("project");
      if (pid && data.find(p=>p.project_id===pid)) setSelectedProject(pid);
      else if (data.length>0) setSelectedProject(data[0].project_id);
    }).catch(()=>toast.error("Failed to load projects")).finally(()=>setLoading(false));
  }, [searchParams]);

  const loadData = useCallback(async () => {
    if (!selectedProject) return;
    try {
      if (tab==="directories") {
        const [subs, ov] = await Promise.all([
          apiClient.get(`/link-builder/submissions/${selectedProject}`),
          apiClient.get(`/link-builder/submissions/${selectedProject}/overview`),
        ]);
        setSubmissions(subs); setOverview(ov);
      } else if (tab==="outreach") {
        setOutreachList(await apiClient.get(`/link-builder/outreach/${selectedProject}`));
      } else if (tab==="opportunities") {
        setOpportunities(await apiClient.get(`/link-builder/opportunities/${selectedProject}`));
      }
    } catch(e) { console.error(e); }
  }, [selectedProject, tab]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmitAll = async () => {
    setSubmitting(true);
    try { await apiClient.post(`/link-builder/submissions/${selectedProject}/submit-all`, {}); toast.success("Submitting to all directories..."); setTimeout(loadData, 2000); }
    catch(e) { toast.error(e.message||"Failed"); }
    finally { setSubmitting(false); }
  };

  const handleSubmitSingle = async (id) => {
    try { await apiClient.post(`/link-builder/submissions/${selectedProject}/submit/${id}`, {}); toast.success("Submitted!"); loadData(); }
    catch(e) { toast.error(e.message||"Failed"); }
  };

  const handleGenerateOutreach = async () => {
    if (!targetUrl.trim()) return;
    setGenerating(true);
    try { await apiClient.post("/link-builder/outreach/generate", { project_id:selectedProject, target_url:targetUrl, target_email:targetEmail||null }); toast.success("Outreach email generated!"); setTargetUrl(""); setTargetEmail(""); loadData(); }
    catch(e) { toast.error(e.message||"Failed"); }
    finally { setGenerating(false); }
  };

  const handleSendOutreach = async (id) => {
    try { await apiClient.post(`/link-builder/outreach/${id}/send`, {}); toast.success("Email sent!"); loadData(); }
    catch(e) { toast.error(e.message||"Failed"); }
  };

  const handleAnalyzeCompetitor = async () => {
    if (!competitorUrl.trim()) return;
    setAnalyzing(true);
    try { await apiClient.post("/link-builder/opportunities/analyze", { project_id:selectedProject, competitor_url:competitorUrl }); toast.success("Analyzing competitor backlinks..."); setCompetitorUrl(""); setTimeout(()=>{ loadData(); setAnalyzing(false); }, 5000); }
    catch(e) { toast.error(e.message||"Failed"); setAnalyzing(false); }
  };

  if (loading) return (
    <div style={{ minHeight:'100vh', background:S.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ width:24, height:24, border:`2px solid ${S.indigo}`, borderTop:'2px solid transparent', borderRadius:'50%' }} className="animate-spin" />
    </div>
  );

  const TABS = [
    { id:"directories",   label:"Directories",  count:submissions.length },
    { id:"outreach",      label:"Outreach",      count:outreachList.length },
    { id:"opportunities", label:"Opportunities", count:opportunities.length },
  ];

  const stats = overview ? [
    { label:"Total",         value:overview.total_directories, color:S.text },
    { label:"Submitted",     value:overview.submitted,         color:'#60a5fa' },
    { label:"Approved",      value:overview.approved,          color:'#4ade80' },
    { label:"Pending",       value:overview.pending_approval,  color:'#fbbf24' },
    { label:"Failed",        value:overview.failed,            color:'#f87171' },
    { label:"Not submitted", value:overview.not_submitted,     color:S.hint },
  ] : [];

  const inp = { padding:'9px 12px', border:`1px solid ${S.border}`, borderRadius:7, fontSize:13, color:S.text, background:S.bg, outline:'none', fontFamily:'inherit' };

  return (
    <div style={{ minHeight:'100vh', background:S.bg, fontFamily:"'IBM Plex Sans', system-ui, sans-serif", color:S.text }}>
      <header style={{ background:S.white, borderBottom:`1px solid ${S.border}`, position:'sticky', top:0, zIndex:50 }}>
        <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 24px' }}>
          <div style={{ height:52, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <button onClick={()=>navigate('/dashboard')}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', background:'transparent', border:`1px solid ${S.border}`, borderRadius:6, fontSize:13, color:S.muted, cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.borderColor=S.borderMd}
                onMouseLeave={e=>e.currentTarget.style.borderColor=S.border}>
                <ArrowLeft style={{ width:13, height:13 }} /> Dashboard
              </button>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:28, height:28, background:'rgba(22,163,74,0.12)', borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Target weight="bold" style={{ width:14, height:14, color:'#4ade80' }} />
                </div>
                <h1 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:16, fontWeight:800, color:S.text, margin:0, letterSpacing:'-0.02em' }}>Link Builder</h1>
              </div>
            </div>
            {projects.length>0 && (
              <select value={selectedProject||""} onChange={e=>setSelectedProject(e.target.value)}
                style={{ padding:'5px 10px', border:`1px solid ${S.border}`, borderRadius:6, fontSize:13, color:S.text, background:S.white, outline:'none' }}>
                {projects.map(p=><option key={p.project_id} value={p.project_id}>{p.name}</option>)}
              </select>
            )}
          </div>
          <nav style={{ display:'flex', borderTop:`1px solid ${S.border}` }}>
            {TABS.map(t=>(
              <button key={t.id} onClick={()=>setTab(t.id)}
                style={{ padding:'10px 18px', fontSize:13, fontWeight:500, background:'transparent', border:'none', borderBottom:`2px solid ${tab===t.id?S.indigo:'transparent'}`, color:tab===t.id?S.text:S.muted, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                {t.label}
                {t.count>0 && <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:10, background:tab===t.id?S.indigo:S.bg, color:tab===t.id?'#fff':S.hint }}>{t.count}</span>}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main style={{ maxWidth:1280, margin:'0 auto', padding:'32px 24px' }}>
        <UpgradeBanner feature="Analyse concurrents, construction liens illimitée" />
        <div style={{ marginBottom:24 }}>
          <h1 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:28, fontWeight:900, color:S.text, margin:0, letterSpacing:'-0.04em' }}>Link Builder</h1>
          <p style={{ fontSize:13, color:S.hint, margin:'5px 0 0' }}>Submit to directories, send outreach emails, and discover competitor backlink opportunities.</p>
        </div>

        {/* DIRECTORIES */}
        {tab==="directories" && (
          <>
            {stats.length>0 && (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:10, marginBottom:20 }}>
                {stats.map(s=>(
                  <div key={s.label} style={{ ...card, padding:'12px 14px', textAlign:'center' }}>
                    <p style={{ fontSize:20, fontWeight:900, color:s.color, margin:0, fontFamily:"'Satoshi', sans-serif" }}>{s.value??0}</p>
                    <p style={{ fontSize:10, color:S.hint, margin:'3px 0 0' }}>{s.label}</p>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
              <p style={{ fontSize:13, fontWeight:700, color:S.text, margin:0 }}>Directory submissions</p>
              <button onClick={handleSubmitAll} disabled={submitting}
                style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 16px', background:submitting?S.hint:S.indigo, color:'#fff', border:'none', borderRadius:7, fontSize:13, fontWeight:600, cursor:submitting?'not-allowed':'pointer' }}>
                {submitting ? <Spinner style={{ width:13, height:13 }} className="animate-spin"/> : <Plus weight="bold" style={{ width:13, height:13 }} />}
                {submitting ? "Submitting..." : "Submit to all directories"}
              </button>
            </div>
            <div style={{ ...card, overflow:'hidden' }}>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:S.bg }}>
                      {['Directory','DA','Category','Status','Action'].map(h=>(
                        <th key={h} style={{ padding:'8px 16px', fontSize:10, fontWeight:700, color:S.hint, textAlign:h==='Directory'?'left':'center', letterSpacing:'0.06em', textTransform:'uppercase', borderBottom:`1px solid ${S.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((s,i)=>(
                      <tr key={s.directory_id} style={{ borderBottom:i<submissions.length-1?`1px solid ${S.border}`:'none' }}
                        onMouseEnter={e=>e.currentTarget.style.background='rgba(124,58,237,0.05)'}
                        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <td style={{ padding:'11px 16px' }}>
                          <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ fontSize:13, fontWeight:600, color:S.indigo, textDecoration:'none' }}>{s.name}</a>
                        </td>
                        <td style={{ padding:'11px 16px', textAlign:'center' }}>
                          <span style={{ fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:4, background:'rgba(124,58,237,0.15)', color:S.indigo }}>{s.domain_authority||'—'}</span>
                        </td>
                        <td style={{ padding:'11px 16px', textAlign:'center', fontSize:12, color:S.muted }}>{s.category}</td>
                        <td style={{ padding:'11px 16px', textAlign:'center' }}><StatusBadge status={s.submission_status} /></td>
                        <td style={{ padding:'11px 16px', textAlign:'center' }}>
                          {s.submission_status==="not_submitted"
                            ? <button onClick={()=>handleSubmitSingle(s.directory_id)} style={{ fontSize:12, fontWeight:600, color:S.indigo, background:'transparent', border:'none', cursor:'pointer' }}>Submit</button>
                            : <a href={s.submit_url} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:S.hint, textDecoration:'none', display:'inline-flex', alignItems:'center', gap:3 }}>Visit <ArrowSquareOut style={{ width:10, height:10 }} /></a>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* OUTREACH */}
        {tab==="outreach" && (
          <>
            <div style={{ ...card, padding:'20px 24px', marginBottom:16 }}>
              <p style={{ fontSize:13, fontWeight:700, color:S.text, margin:'0 0 14px' }}>Generate outreach email</p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
                <input value={targetUrl} onChange={e=>setTargetUrl(e.target.value)} placeholder="Target blog URL (e.g. https://blog.example.com)" style={inp}
                  onFocus={e=>e.target.style.borderColor=S.indigo} onBlur={e=>e.target.style.borderColor=S.border} />
                <input value={targetEmail} onChange={e=>setTargetEmail(e.target.value)} placeholder="Contact email (optional)" style={inp}
                  onFocus={e=>e.target.style.borderColor=S.indigo} onBlur={e=>e.target.style.borderColor=S.border} />
              </div>
              <button onClick={handleGenerateOutreach} disabled={generating||!targetUrl.trim()}
                style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 16px', background:generating?S.hint:S.indigo, color:'#fff', border:'none', borderRadius:7, fontSize:13, fontWeight:600, cursor:generating?'not-allowed':'pointer' }}>
                {generating ? <Spinner style={{ width:13, height:13 }} className="animate-spin"/> : <PaperPlaneTilt weight="bold" style={{ width:13, height:13 }} />}
                {generating ? "Generating..." : "Generate email"}
              </button>
            </div>
            <div style={{ ...card, overflow:'hidden' }}>
              <div style={{ padding:'12px 18px', borderBottom:`1px solid ${S.border}` }}>
                <p style={{ fontSize:13, fontWeight:700, color:S.text, margin:0 }}>Outreach emails <span style={{ fontSize:12, fontWeight:500, color:S.hint, marginLeft:6 }}>({outreachList.length})</span></p>
              </div>
              {outreachList.length===0 ? (
                <div style={{ padding:'48px 24px', textAlign:'center' }}>
                  <PaperPlaneTilt style={{ width:28, height:28, color:S.hint, margin:'0 auto 10px' }} />
                  <p style={{ fontSize:13, color:S.hint, margin:0 }}>No outreach emails yet — generate your first above</p>
                </div>
              ) : (
                <div>
                  {outreachList.map((o,i)=>(
                    <div key={o.outreach_id} style={{ padding:'16px 20px', borderBottom:i<outreachList.length-1?`1px solid ${S.border}`:'none' }}>
                      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:10 }}>
                        <div>
                          <a href={o.target_url} target="_blank" rel="noopener noreferrer" style={{ fontSize:13, fontWeight:600, color:S.indigo, textDecoration:'none' }}>{o.target_url}</a>
                          <p style={{ fontSize:11, color:S.hint, margin:'3px 0 0' }}>{o.target_email||"No email set"} · {o.subject}</p>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                          <StatusBadge status={o.status} />
                          {o.status==="draft" && o.target_email && (
                            <button onClick={()=>handleSendOutreach(o.outreach_id)} style={{ fontSize:12, fontWeight:600, color:S.indigo, background:'transparent', border:'none', cursor:'pointer' }}>Send</button>
                          )}
                        </div>
                      </div>
                      <pre style={{ fontSize:11, color:S.muted, background:S.bg, padding:'12px 14px', borderRadius:7, overflow:'auto', maxHeight:100, whiteSpace:'pre-wrap', margin:0, fontFamily:'inherit', lineHeight:1.6 }}>{o.message}</pre>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* OPPORTUNITIES */}
        {tab==="opportunities" && (
          <>
            <div style={{ ...card, padding:'20px 24px', marginBottom:16 }}>
              <p style={{ fontSize:13, fontWeight:700, color:S.text, margin:'0 0 4px' }}>Analyze competitor backlinks</p>
              <p style={{ fontSize:12, color:S.hint, margin:'0 0 14px' }}>Discover sites that link to your competitors — then reach out to get the same links.</p>
              <div style={{ display:'flex', gap:10 }}>
                <input value={competitorUrl} onChange={e=>setCompetitorUrl(e.target.value)} placeholder="Competitor URL (e.g. https://competitor.com)" style={{ ...inp, flex:1 }}
                  onFocus={e=>e.target.style.borderColor=S.indigo} onBlur={e=>e.target.style.borderColor=S.border} />
                <button onClick={handleAnalyzeCompetitor} disabled={analyzing||!competitorUrl.trim()||user?.plan==='free'}
                  style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 16px', background:(analyzing||user?.plan==='free')?S.hint:S.indigo, color:'#fff', border:'none', borderRadius:7, fontSize:13, fontWeight:600, cursor:(analyzing||user?.plan==='free')?'not-allowed':'pointer' }}>
                  {analyzing ? <Spinner style={{ width:13, height:13 }} className="animate-spin"/> : <MagnifyingGlass weight="bold" style={{ width:13, height:13 }} />}
                  {analyzing ? "Analyzing..." : "Analyze"}
                </button>
              </div>
              {user?.plan==='free' && (
                <p style={{ fontSize:11, color:'#f59e0b', margin:'8px 0 0' }}>⚠ Competitor analysis requires Pro or Agency plan. <button onClick={()=>navigate('/pricing')} style={{ color:S.indigo, background:'none', border:'none', cursor:'pointer', fontSize:11, fontWeight:600 }}>Upgrade →</button></p>
              )}
            </div>
            <div style={{ ...card, overflow:'hidden' }}>
              <div style={{ padding:'12px 18px', borderBottom:`1px solid ${S.border}` }}>
                <p style={{ fontSize:13, fontWeight:700, color:S.text, margin:0 }}>Opportunities <span style={{ fontSize:12, fontWeight:500, color:S.hint, marginLeft:6 }}>({opportunities.length})</span></p>
              </div>
              {opportunities.length===0 ? (
                <div style={{ padding:'48px 24px', textAlign:'center' }}>
                  <Target style={{ width:28, height:28, color:S.hint, margin:'0 auto 10px' }} />
                  <p style={{ fontSize:13, color:S.hint, margin:0 }}>Analyze a competitor to discover backlink opportunities</p>
                </div>
              ) : (
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr style={{ background:S.bg }}>
                        {['Source','Type','Difficulty','Status','Action'].map(h=>(
                          <th key={h} style={{ padding:'8px 16px', fontSize:10, fontWeight:700, color:S.hint, textAlign:h==='Source'?'left':'center', letterSpacing:'0.06em', textTransform:'uppercase', borderBottom:`1px solid ${S.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {opportunities.map((o,i)=>(
                        <tr key={o.opportunity_id||o.source_domain} style={{ borderBottom:i<opportunities.length-1?`1px solid ${S.border}`:'none' }}
                          onMouseEnter={e=>e.currentTarget.style.background='rgba(124,58,237,0.05)'}
                          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                          <td style={{ padding:'11px 16px' }}>
                            <a href={o.source_url} target="_blank" rel="noopener noreferrer" style={{ fontSize:13, fontWeight:600, color:S.indigo, textDecoration:'none' }}>{o.source_domain}</a>
                          </td>
                          <td style={{ padding:'11px 16px', textAlign:'center', fontSize:12, color:S.muted }}>{o.link_type?.replace(/_/g,' ')}</td>
                          <td style={{ padding:'11px 16px', textAlign:'center' }}>
                            <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:5,
                              background:o.difficulty==='easy'?'rgba(21,128,61,0.2)':o.difficulty==='medium'?'rgba(161,98,7,0.2)':'rgba(185,28,28,0.2)',
                              color:o.difficulty==='easy'?'#4ade80':o.difficulty==='medium'?'#fbbf24':'#f87171' }}>
                              {o.difficulty}
                            </span>
                          </td>
                          <td style={{ padding:'11px 16px', textAlign:'center' }}><StatusBadge status={o.status} /></td>
                          <td style={{ padding:'11px 16px', textAlign:'center' }}>
                            <button onClick={()=>{ setTab("outreach"); setTargetUrl(o.source_url); }} style={{ fontSize:12, fontWeight:600, color:S.indigo, background:'transparent', border:'none', cursor:'pointer' }}>Outreach</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
